import { gql, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CatalogSectionHeader } from '@/modules/catalog/CatalogSectionHeader';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { KIT_ITEM_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { formatCentsString, majorStringToCents } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useAuthStore } from '@/stores/authStore';
import { KitItemsEditor } from './KitItemsEditor';

const KITS_QUERY = gql`
  query Kits($offset: Int, $limit: Int, $search: String, $status_filter: CatalogStatusFilter) {
    kits(offset: $offset, limit: $limit, search: $search, status_filter: $status_filter) {
      items {
        id
        code
        name
        description
        price_cents
        pricing_mode
        status
        items {
          id
          item_kind
          product_id
          service_id
          quantity
        }
      }
      page {
        total
        offset
        limit
        has_more
      }
    }
  }
`;

const SET_KIT_STATUS_MUTATION = gql`
  mutation SetKitStatus($id: ID!, $status: String!) {
    setKitStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

const CREATE_KIT_MUTATION = gql`
  mutation CreateKit($input: CreateKitInput!) {
    createKit(input: $input) {
      id
    }
  }
`;

interface Kit {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price_cents: string;
  pricing_mode: 'bundle_fixed' | 'componentized';
  status: 'active' | 'inactive' | 'discontinued';
  items: { id: string; item_kind: string; quantity: string }[];
}

interface KitsResponse {
  kits: {
    items: Kit[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function KitsPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_write = user?.permissions.includes('catalog.products.write') ?? false;
  const [editing_kit, setEditingKit] = useState<{ id: string; name: string } | null>(null);
  const [show_create, setShowCreate] = useState(false);
  const [create_code, setCreateCode] = useState('');
  const [create_name, setCreateName] = useState('');
  const [create_description, setCreateDescription] = useState('');
  const [create_price, setCreatePrice] = useState('0.00');
  const [create_error, setCreateError] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery<KitsResponse>(KITS_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
    },
  });

  const [setKitStatus] = useMutation(SET_KIT_STATUS_MUTATION);
  const [createKit, create_state] = useMutation(CREATE_KIT_MUTATION);
  const rows = (data?.kits.items ?? []).map((kit) => ({
    ...kit,
    items: kit.items.map((item) => ({
      ...item,
      item_kind:
        KIT_ITEM_KIND_FROM_GQL[item.item_kind as keyof typeof KIT_ITEM_KIND_FROM_GQL] ??
        item.item_kind,
    })),
  }));

  const onClickToggleStatus = async (kit: Kit): Promise<void> => {
    await setKitStatus({
      variables: { id: kit.id, status: kit.status === 'active' ? 'inactive' : 'active' },
    });
    await refetch();
  };

  const resetCreate = (): void => {
    setCreateCode('');
    setCreateName('');
    setCreateDescription('');
    setCreatePrice('0.00');
    setCreateError(null);
  };

  const onSubmitCreate = async (): Promise<void> => {
    setCreateError(null);
    if (create_code.trim() === '' || create_name.trim() === '') {
      setCreateError(t('kits.create_error_required'));
      return;
    }
    try {
      await createKit({
        variables: {
          input: {
            code: create_code.trim(),
            name: create_name.trim(),
            description: create_description.trim() === '' ? null : create_description.trim(),
            price_cents: majorStringToCents(create_price),
            pricing_mode: 'COMPONENTIZED',
          },
        },
      });
      setShowCreate(false);
      resetCreate();
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('kits.create_error_generic');
      setCreateError(message);
    }
  };

  const columns: CrudColumn<Kit>[] = [
    {
      key: 'code',
      header_key: 'kits.code',
      width: 'w-32',
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      key: 'name',
      header_key: 'kits.name',
      render: (r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          {r.description ? (
            <div className="text-muted-foreground text-xs">{r.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'items',
      header_key: 'kits.items',
      align: 'right',
      width: 'w-24',
      render: (r) => r.items.length,
    },
    {
      key: 'price',
      header_key: 'kits.price',
      align: 'right',
      width: 'w-32',
      render: (r) => formatCentsString(r.price_cents),
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-64',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditingKit({ id: r.id, name: r.name })}
            >
              {t('kit_items.edit_action')}
            </Button>
          ) : null}
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onClickToggleStatus(r);
              }}
            >
              {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <CatalogSectionHeader title={t('kits.title')} subtitle={t('kits.subtitle')} />

      <CrudTable<Kit>
        rows={rows}
        columns={columns}
        loading={loading}
        page={data?.kits.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="kits.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            primary_action={
              can_write ? (
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreate((v) => !v);
                    setCreateError(null);
                  }}
                >
                  {t('kits.create')}
                </Button>
              ) : null
            }
          />
        }
      />

      {show_create ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('kits.create')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>{t('kits.code')}</Label>
                <Input value={create_code} onChange={(e) => setCreateCode(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>{t('kits.name')}</Label>
                <Input value={create_name} onChange={(e) => setCreateName(e.target.value)} />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>{t('catalog.item_form.description')}</Label>
                <Input
                  value={create_description}
                  onChange={(e) => setCreateDescription(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>{t('kits.price')}</Label>
                <Input value={create_price} onChange={(e) => setCreatePrice(e.target.value)} />
              </div>
            </div>
            {create_error ? <p className="mt-3 text-sm text-destructive">{create_error}</p> : null}
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCreate(false);
                resetCreate();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={() => void onSubmitCreate()} disabled={create_state.loading}>
              {t('common.save')}
            </Button>
          </CardFooter>
        </Card>
      ) : null}

      {editing_kit ? (
        <KitItemsEditor
          kit_id={editing_kit.id}
          kit_name={editing_kit.name}
          onClose={() => {
            setEditingKit(null);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}
